import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { EditorSelection, EditorState, RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView, keymap, placeholder as placeholderExtension } from '@codemirror/view';
import { Completion, LookupTree } from '../types/types';
import { findCompletions, resolveNonOverlappingImplicitLinkSpans, ImplicitLinkMatchPosition } from '../biz/linkFinder';

type CaretCoordinates = { top: number, left: number } | null;

export type ActiveLink = { id: string, top: number, left: number } | null;

type UseCodeMirrorEditorArgs = {
  containerRef: RefObject<HTMLDivElement | null>,
  placeholder: string,
  text: string,
  setText: Dispatch<SetStateAction<string>>,
  itemLookupTree: LookupTree,
  itemId: string,
  readOnly: boolean,
  setSuggestions: Dispatch<SetStateAction<Completion[]>>,
  setCaretCoordinates: Dispatch<SetStateAction<CaretCoordinates>>,
  setActiveLink: Dispatch<SetStateAction<ActiveLink>>,
};

type ImplicitLinkDecorationsPayload = {
  linkSpans: ImplicitLinkMatchPosition[],
};

const implicitLinkDecorationsEffect = StateEffect.define<ImplicitLinkDecorationsPayload>();

const buildImplicitLinkDecorations = (matches: ImplicitLinkMatchPosition[]) => {
  const builder = new RangeSetBuilder<Decoration>();
  for (const match of matches) {
    if (match.unencodedEnd < match.unencodedStart) {
      continue;
    }
    const to = match.unencodedEnd + 1;
    builder.add(
      match.unencodedStart,
      to,
      Decoration.mark({
        class: `implicitlink ${match.type}`,
        attributes: { 'data-id': match.id },
      })
    );
  }
  return builder.finish();
};

const implicitLinkDecorationsField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, transaction) {
    let nextDecorations = decorations.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (effect.is(implicitLinkDecorationsEffect)) {
        nextDecorations = buildImplicitLinkDecorations(effect.value.linkSpans);
      }
    }
    return nextDecorations;
  },
  provide: field => EditorView.decorations.from(field),
});

function useCodeMirrorEditor({
  containerRef,
  placeholder,
  text, // We should probably overhaul how the text state is synced and passed back and forth
  setText,
  itemLookupTree,
  itemId,
  readOnly,
  setSuggestions,
  setCaretCoordinates,
  setActiveLink,
}: UseCodeMirrorEditorArgs) {
  const editorRef = useRef<EditorView | null>(null);
  const itemLookupTreeRef = useRef(itemLookupTree);
  const itemIdRef = useRef(itemId);
  const textRef = useRef(text);

  const updateCaretCoordinates = useCallback((editor: EditorView) => {
    const caretPosition = editor.state.selection.main.head;
    const coords = editor.coordsAtPos(caretPosition);
    if (!coords) {
      return;
    }
    setCaretCoordinates({
      top: coords.top,
      left: coords.left
    });
  }, [setCaretCoordinates]);

  const updateActiveLink = useCallback((editor: EditorView) => {
    const selection = editor.state.selection.main;
    const found = { id: null as string | null };
    if (editor.hasFocus && selection.empty) {
      editor.state.field(implicitLinkDecorationsField).between(0, editor.state.doc.length, (from, to, value) => {
        if (selection.head >= from && selection.head <= to) {
          found.id = value.spec.attributes['data-id'];
          return false;
        }
      });
    }
    const coords = found.id ? editor.coordsAtPos(selection.head) : null;
    setActiveLink(found.id && coords ? { id: found.id, top: coords.bottom, left: coords.left } : null);
  }, [setActiveLink]);

  const applyImplicitLinkHighlighting = useCallback((editor: EditorView, editorText: string) => {
    const linkSpans = resolveNonOverlappingImplicitLinkSpans(itemLookupTreeRef.current, editorText, itemIdRef.current);
    editor.dispatch({
      effects: implicitLinkDecorationsEffect.of({ linkSpans }),
    });
  }, []);

  useEffect(() => {
    itemLookupTreeRef.current = itemLookupTree;

    const editor = editorRef.current;
    if (editor) {
      applyImplicitLinkHighlighting(editor, editor.state.doc.toString());
    }
  }, [itemLookupTree, applyImplicitLinkHighlighting]);

  useEffect(() => {
    itemIdRef.current = itemId;

    const editor = editorRef.current;
    if (editor) {
      applyImplicitLinkHighlighting(editor, editor.state.doc.toString());
    }
  }, [itemId, applyImplicitLinkHighlighting]);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const newText = update.state.doc.toString();
        textRef.current = newText;
        setText(newText);

        const caretPosition = update.state.selection.main.head;
        const textBeforeCaret = newText.substring(0, caretPosition);
        const completion = findCompletions(itemLookupTreeRef.current, textBeforeCaret);
        setSuggestions(completion);
      }

      if (update.selectionSet && !update.docChanged) {
        setSuggestions([]);
      }

      if (update.docChanged || update.selectionSet) {
        updateCaretCoordinates(update.view);
      }

      const decorationsReplaced = update.transactions.some(tr => tr.effects.some(e => e.is(implicitLinkDecorationsEffect)));
      if (update.docChanged || update.selectionSet || update.focusChanged || decorationsReplaced) {
        updateActiveLink(update.view);
      }
    });

    const state = EditorState.create({
      doc: textRef.current,
      extensions: [
        // Android IME Enter is only reliably handled by CodeMirror when a keymap binds it
        keymap.of([{
          key: 'Enter',
          run: (view) => {
            if (view.state.readOnly) {
              return false;
            }
            view.dispatch(view.state.update(view.state.replaceSelection('\n'), { scrollIntoView: true, userEvent: 'input' }));
            return true;
          },
        }]),
        EditorView.lineWrapping,
        implicitLinkDecorationsField,
        updateListener,
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
        placeholderExtension(placeholder),
      ],
    });

    const editor = new EditorView({
      state,
      parent: containerRef.current,
    });

    editorRef.current = editor;
    applyImplicitLinkHighlighting(editor, textRef.current);
    updateCaretCoordinates(editor);

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, [placeholder, applyImplicitLinkHighlighting, containerRef, readOnly, setSuggestions, setText, updateCaretCoordinates, updateActiveLink]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    if (editor.state.doc.toString() !== text) {
      const currentSelection = editor.state.selection.main;
      const maxAnchor = text.length;
      const anchor = Math.min(currentSelection.anchor, maxAnchor);
      const head = Math.min(currentSelection.head, maxAnchor);
      editor.dispatch({
        changes: {
          from: 0,
          to: editor.state.doc.length,
          insert: text,
        },
        selection: EditorSelection.range(anchor, head),
      });
    }
    applyImplicitLinkHighlighting(editor, text);
  }, [text, applyImplicitLinkHighlighting]);

  return editorRef;
}

export default useCodeMirrorEditor;